import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, OldMainNav, Typography } from "@payping/web-components";
import { User } from "@payping/web-components/components/MainNav/types";
import { safeStr } from "@payping/utils/string";
import { useAsync } from "react-use";
import { webConstants } from "@payping/utils/platformConstants";
import {
  CloudSnow,
  HelpRing,
  HomeOutlined,
  LogoutOutlined,
  SettingOutlined,
  User as UserOutlined,
} from "@payping/icons";
import { getUserMessages, getUserProfile, getUserWage } from "@payping/redux/actions";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { referralMenuDropdownId } from "@/assets/googleTrackIds";
import { setUpdateAvailable } from "@/redux/actions/serviceWorker";
import NewVersionModal from "./subSection/NewVersionModal";
import Notifications from "./subSection/Notifications";
import Search from "./subSection/Search";
import Version from "./subSection/Version";
import styles from "../../assets/scss/Profile.module.scss";
import { UserSection } from "./subSection/User";
import { appIcons } from "./subSection/DownloadAppIcons";
import { useSelectedTab } from "./useSelectedTab";
import Link from "./Link";

const AppMainNav: React.FC = () => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const selectedTab = useSelectedTab();
  const [visibleSearch, setVisibleSearch] = useState<boolean>(false);
  const personalInfo = useAppSelector((state) => state.user.personalInfo);
  const isSettlement = useAppSelector((state) => state.user.isSettlement);
  const messageData = useAppSelector((state) => state.user.message);
  const hasUpdate = useAppSelector((state) => state.serviceWorker.hasUpdate);
  const isReferralActive = useAppSelector((state) => state.user.isReferralActive);
  const navigate = useNavigate();
  const { HELP_URL, DashboardRoutes } = webConstants;

  useAsync(() => dispatch(getUserMessages({ limit: 5, offset: 0 })), [dispatch]);
  useAsync(() => dispatch(getUserProfile()), [dispatch]);
  useAsync(() => dispatch(getUserWage()), [dispatch]);

  useEffect(() => {
    if (personalInfo) {
      const { email, id, username, businessName, firstName, lastName } = personalInfo;
      Sentry.configureScope((scope) => {
        scope.setUser({ email, id: id?.toString(), username });
        scope.setExtra("firstName", firstName);
        scope.setExtra("lastName", lastName);
        scope.setExtra("businessName", businessName);
      });
    }
  }, [personalInfo]);

  useEffect(() => {
    navigator.serviceWorker?.ready?.then((reg) => {
      if (reg.waiting) {
        dispatch(setUpdateAvailable(true));
      }
    });
  }, [dispatch]);

  function closeSearch() {
    setVisibleSearch(false);
  }

  const tabPanesCategory1 = [
    <Link key={DashboardRoutes.home.root} to={DashboardRoutes.home.root}>
      <span className="align-center">
        <HomeOutlined width={"18px"} />
      </span>
    </Link>,
    <Link key={DashboardRoutes.report.root} to={DashboardRoutes.report.root}>
      {t("tabs.report")}
    </Link>,
    <Link key={DashboardRoutes.ipg.root} to={DashboardRoutes.ipg.root}>
      {t("tabs.ipg")}
    </Link>,
    ...(selectedTab === DashboardRoutes.platform.root
      ? [
          <Link key={DashboardRoutes.platform.root} to={DashboardRoutes.platform.root}>
            {t("tabs.platform")}
          </Link>,
        ]
      : []),
    <Link key={DashboardRoutes.customer.root} to={DashboardRoutes.customer.root}>
      {t("tabs.customer")}
    </Link>,
    <Link key={DashboardRoutes.product.root} to={DashboardRoutes.product.root}>
      {t("tabs.product")}
    </Link>,
    <Link key={DashboardRoutes.invoice.root} to={DashboardRoutes.invoice.root}>
      {t("tabs.invoice")}
    </Link>,
    <Link key={DashboardRoutes.user.root} to={DashboardRoutes.user.root} hasNotifyPoint={!isReferralActive}>
      {t("tabs.settings")}
    </Link>,
    ...(isSettlement
      ? [
          <Link key={DashboardRoutes.settlement.root} to={DashboardRoutes.settlement.root}>
            {t("tabs.settlement")}
          </Link>,
        ]
      : []),
  ];

  const tabPanes = [tabPanesCategory1];

  const fullName = safeStr(
    personalInfo?.businessName || `${safeStr(personalInfo?.firstName)} ${safeStr(personalInfo?.lastName)}`
  );
  const user: User = {
    fullName,
    onPress: () => navigate(DashboardRoutes.user.profile.root),
    profileImg: personalInfo?.profilePicture,
    status: personalInfo?.verifyType,
    username: `@${safeStr(personalInfo?.username)}`,
  };

  const menu = (
    <Menu className={styles.profileMenu} triggerSubMenuAction={"click"}>
      <Menu.Item key={"1"} onClick={user.onPress}>
        <UserOutlined width={"18px"} />
        <Typography.Text className={styles.profileMenuItemTitle}>{` ${t("profileMenu.profile")}`}</Typography.Text>
      </Menu.Item>

      <Menu.Item
        data-google-track={referralMenuDropdownId}
        key={"2"}
        onClick={() => navigate(DashboardRoutes.user.referral)}
      >
        <CloudSnow width={"18px"} />
        <Typography.Text className={styles.profileMenuItemTitle}>{` ${t("profileMenu.earnMoney")}`}</Typography.Text>
      </Menu.Item>

      {/*
      TODO: add multiple themes to application
       <Menu.Item key={"2"} onClick={() => dispatch(setTheme(theme === "dark" ? "light" : "dark"))}>
        {theme === "dark" ? (
          <>
            <SunOutlined width={"18px"} />{` ${t("profileMenu.theme.light")}`}
          </>
        ) : (
          <>
            <MoonOutlined width={"18px"} />{` ${t("profileMenu.theme.dark")}`}
          </>
        )}
       </Menu.Item>
      */}
      <Menu.Item key={"3"} onClick={() => navigate(DashboardRoutes.user.settings.root)}>
        <SettingOutlined width={"18px"} />
        <Typography.Text className={styles.profileMenuItemTitle}>{` ${t("profileMenu.setting")}`}</Typography.Text>
      </Menu.Item>
      <Menu.Item key={"4"} onClick={() => window.open(HELP_URL, "_blank")}>
        <HelpRing width={"18px"} />
        <Typography.Text className={styles.profileMenuItemTitle}>{` ${t("profileMenu.help")}`}</Typography.Text>
      </Menu.Item>
      <Menu.Item key={"5"} onClick={() => navigate("/logout")}>
        <LogoutOutlined width={"18px"} />
        <Typography.Text className={styles.profileMenuItemTitle}>{` ${t("profileMenu.exit")}`}</Typography.Text>
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <OldMainNav
        userEl={<UserSection menu={menu} user={user} />}
        items={tabPanes}
        onItemClick={closeSearch}
        selected={selectedTab}
        version={hasUpdate ? <NewVersionModal /> : <Version />}
        appIcons={appIcons}
        search={{
          active: visibleSearch,
          onClick: () => setVisibleSearch((prev) => !prev),
        }}
        notifications={{
          content: <Notifications messageList={messageData?.list.filter((_, index) => index < 5)} />,
          newNotificationsCount: messageData?.list[0]?.isSeen === false ? 1 : 0,
        }}
        upgradeRoute={DashboardRoutes.user.upgrade}
      />
      <Search visible={visibleSearch} setVisible={setVisibleSearch} />
    </>
  );
};

export default AppMainNav;
